import { OutlinedInput, Stack, Typography } from '@mui/material';
import { FormField } from '../form/FormField';
import { graphql } from '../gql';
import { useModalContext } from '../modal/ModalContext';
import { ModalActions } from '../modal/components/ModalActions';
import { ModalContent } from '../modal/components/ModalContent';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { z } from 'zod';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';
import { useSnackbar } from '../store/snackbar';

const journey_DeletePhase = graphql(`
	mutation journey_DeletePhase($id: UUID!) {
		deletePhase(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const getPhase = graphql(`
	query journey_getPhase($id: UUID!) {
		getPhase(by: { id: $id }) {
			touchpoints {
				id
			}
		}
	}
`);

const useDeletePhase = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { mutate } = useApiMutation(journey_DeletePhase, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.deletePhase.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'phase.success.deleted' }),
					error: intl.formatMessage({ id: 'phase.error.delete' }),
				},
				errorMessage: result.deletePhase.errorMessage,
				openSnackbar,
			}),
	});

	return { mutate };
};

export const deletePhaseModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const createFormSchema = (textToMatch: string, intl: IntlShape) =>
	z.object({
		confirmField: z.literal(textToMatch, {
			errorMap: () => ({ message: intl.formatMessage({ id: 'common.delete.invalid' }, { desired: textToMatch }) }),
		}),
	});

export const DeletePhaseModal: FC<z.infer<typeof deletePhaseModalSchema>> = ({ id, name }) => {
	const intl = useIntl();
	const { mutate: deleteJourney } = useDeletePhase();
	const { data } = useApiQuery(getPhase, { variables: { id } });
	const { closeModal } = useModalContext();
	const form = useTypedForm({
		schema: createFormSchema(name, intl),
	});

	const requiresConfirmation = data?.getPhase ? data.getPhase.touchpoints.length > 0 : false;

	return (
		<>
			<ModalContent
				isDelete
				requiresConfirmation={requiresConfirmation}
				title={intl.formatMessage({ id: 'phase.delete-modal.title' })}
				description={intl.formatMessage({ id: 'phase.delete-modal.description' }, { name })}
			>
				<>
					<Typography textAlign={'center'}>
						<strong>
							<FormattedMessage id={'common.delete.confirmation'} />
						</strong>
					</Typography>
					<Stack width={{ xs: '100%', sm: '50%' }} alignSelf={'center'}>
						<form
							noValidate
							onSubmit={(e) => {
								e.preventDefault();
								if (form.formState.isValid) {
									deleteJourney({ id });
									closeModal();
								}
							}}
						>
							<FormField
								form={form}
								name={'confirmField'}
								label={''}
								render={(props) => (
									<OutlinedInput {...props} placeholder={intl.formatMessage({ id: 'common.delete.confirmation-placeholder' }, { name })} />
								)}
							/>
						</form>
					</Stack>
				</>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteJourney({ id });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
				disablePrimaryButton={requiresConfirmation ? !form.formState.isValid : false}
			/>
		</>
	);
};
